import React, { useState, useEffect, useContext } from 'react';
import { DataContext } from '../../components/DataContext';
import { PlayingCard } from './Cards';
import { GameRecap } from './gameRecap';
import { PlayerStreaks } from './playerStreaks';
import { getLastGameRecap } from '../../components/parseData';
import '../../styles/main.css';

const formatCurrency = (value) => {
  const numValue = Number(value);
  const absValue = Math.abs(numValue).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return numValue < 0 ? `-$${absValue}` : `$${absValue}`;
};

// Generic animation hook for numeric values
const useNumericAnimation = (targetValue, showDecimals = false) => {
  const [displayedValue, setDisplayedValue] = useState(0);

  useEffect(() => {
    if (displayedValue === targetValue) return;
  
    const increment = Math.max(1, Math.abs(targetValue - displayedValue) / 25);
    const interval = setInterval(() => {
      setDisplayedValue((prev) => {
        const nextValue = prev + increment * Math.sign(targetValue - prev);
        if ((targetValue > prev && nextValue >= targetValue) || (targetValue < prev && nextValue <= targetValue)) {
          clearInterval(interval);
          return targetValue;
        }
        return nextValue;
      });
    }, 16);
  
    return () => clearInterval(interval);
  }, [targetValue, displayedValue]);
  
  return showDecimals ? displayedValue.toFixed(2) : Math.round(displayedValue);
};

export default function Home() {
  const [totalMoneyIn, setTotalMoneyIn] = useState(0);
  const [totalGames, setTotalGames] = useState(0);
  const [uniquePlayers, setUniquePlayers] = useState(0);
  const [uniqueLocations, setUniqueLocations] = useState(0);
  const [lastGameData, setLastGameData] = useState({});
  
  // Use the animation hook for different values
  const displayedMoneyIn = useNumericAnimation(totalMoneyIn, false);
  const displayedTotalGames = useNumericAnimation(totalGames, false);
  const displayedUniquePlayers = useNumericAnimation(uniquePlayers, false);
  const displayedUniqueLocations = useNumericAnimation(uniqueLocations, false);

  const [cardsLoaded, setCardsLoaded] = useState(false);
  const { gamesData, playersData } = useContext(DataContext);

  useEffect(() => {
    const calculateHomePageStats = async () => {
      try {
        if (gamesData && playersData) {
          const gamesArray = Object.values(gamesData);
          const playersArray = Object.values(playersData);
  
          const totalMoney = gamesArray.reduce((sum, game) => sum + game.moneyOnTable, 0);
          const uniquePlayerCount = new Set(playersArray.map(player => player.playerName)).size;
          
          // Calculate unique locations
          const uniqueLocationCount = new Set(gamesArray.map(game => game.location).filter(location => location)).size;
  
          setTotalMoneyIn(totalMoney);
          setTotalGames(gamesArray.length);
          setUniquePlayers(uniquePlayerCount);
          setUniqueLocations(uniqueLocationCount);

          // Get last game recap data
          const lastGame = await getLastGameRecap();
          setLastGameData(lastGame);
        }
      } catch (error) {
        console.error('Error calculating stats:', error);
      }
    };
  
    calculateHomePageStats();
  }, [gamesData, playersData]);

  useEffect(() => {
    // Trigger card animations after component mounts
    const timer = setTimeout(() => setCardsLoaded(true));
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="home-container">
      {/* Header - using original styles */}
      <div className ="home-text">
        <h1 className="h1-lobster">Degenerate</h1>
        <h2 className="h2-lobster">Poker League</h2>
      </div>

      {/*White separator line*/}
      <hr className="separator-line" />

      {/* Playing Cards - Fixed size with dynamic columns */}
      <div className="cards-container">

        <PlayingCard
          value={formatCurrency(displayedMoneyIn)}
          label="Dollars Staked"
          suit="diamonds"
          isAnimating={cardsLoaded}
        />
        <PlayingCard
          value={displayedTotalGames}
          label="Games Tracked"
          suit="clubs"
          isAnimating={cardsLoaded}
        />
        <PlayingCard
          value={displayedUniquePlayers}
          label="Unique Players"
          suit="hearts"
          isAnimating={cardsLoaded}
        />
        <PlayingCard
          value={displayedUniqueLocations}
          label="Unique Locations"
          suit="spades"
          isAnimating={cardsLoaded}
        />
      </div>

       {/*White separator line*/}
      <hr className="separator-line" />

      {/* Game Recap Section */}
      <GameRecap gameData={lastGameData} />

      {/*White separator line*/}
      <hr className="separator-line" />

      {/* Player Streaks Section */}
      <PlayerStreaks streaksData={lastGameData?.streaks} />

      {/*White separator line*/}
      <hr className="separator-line" />

      {/* Footer */}
      <div className="dev-mark">© 2022-2025 Kyle James & Ishan Sandhu</div>
    </div>
  );
}